import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { AppState } from 'react-native';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import FastenSearch, { FastenSearchSelection } from '../../../../shared/FastenSearch';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import usePreRedirectArgs from '../../../../../hooks/usePreRedirectArgs';
import useFastenConnect from '../../../../../hooks/api/useFastenConnect';
import { useBuildFastenConnectUrl } from '../../../../../hooks/useBuildFastenConnectUrl';
import { openURL } from 'expo-linking';
import { usePromptButton } from '../../../../shared/PromptButton';
import { ContentStack } from '../../../../v2BaseComponents/Stack';
import { PromptActions } from '../../../../v2BaseComponents/Prompt';
import Text from '../../../../v2BaseComponents/Text';
import useNotify from '../../../../../hooks/useNotify';
import { supportEmail } from '../../../../../utils/constants';

const intl = getIntl('profile-add-fasten-portal', {
    addFastenPortal: 'Connect with Fasten',
    unableToFindPortal: 'Unable to find your Portal?',
    unableToFindPortalMessage:
        'We currently have connections to thousands of health systems. If you are unable to find your Portal, please email us at {email}. We will do our best to help!',
    emailSupport: 'Email Support',
    unableToOpenEmailApp: 'Unable to open email app',
    subject: 'Add Portal Request',
});

export type AddFastenPortalScreenOwnProps = {};

export type AddFastenPortalScreenProps = AddFastenPortalScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'add-fasten-portal'>;
};

const AddFastenPortalScreen: React.FC<AddFastenPortalScreenProps> = (props) => {
    const recordEvent = useRecordEvent();
    const notify = useNotify();
    const navigation = useProfilesTabNavigation();
    const medicalProfileId = props.route.params.profileId;
    const [hasSearched, setHasSearched] = React.useState(false);
    // Set when a connect is started so we know, on returning from the external OAuth browser, to send
    // the user to the connections list (see the AppState effect below).
    const connectInitiatedRef = React.useRef(false);

    useSetHeaderTitle(intl('addFastenPortal'));

    const { setPreRedirectArgs } = usePreRedirectArgs();
    const { buildFastenConnectUrl } = useBuildFastenConnectUrl();

    const unableToFindPortalPrompt = usePromptButton({
        promptTitle: intl('unableToFindPortal'),
        renderPromptContents: () => (
            <ContentStack>
                <Text variant="body-1">{intl('unableToFindPortalMessage', { email: supportEmail })}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('emailSupport'),
                            onPress: () => {
                                openURL(`mailto:${encodeURIComponent(supportEmail)}?subject=${intl('subject')}`).catch(
                                    () => {
                                        notify('error', intl('unableToOpenEmailApp'));
                                    },
                                );
                            },
                            icon: 'Send',
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    // Track the selected brand metadata so we can store it in preRedirectArgs
    const pendingMetaRef = React.useRef({ brandId: '', brandName: '', platformType: 'unknown' });

    const { connect, isConnecting } = useFastenConnect({
        medicalProfileId,
        onConnectUrlReceived: (redirectUrl) => {
            const meta = pendingMetaRef.current;
            setPreRedirectArgs(
                {
                    type: 'fasten-connect',
                    profileId: medicalProfileId,
                    brandId: meta.brandId,
                    brandName: meta.brandName,
                    platformType: meta.platformType,
                },
                () => {
                    connectInitiatedRef.current = true;
                    openURL(buildFastenConnectUrl(redirectUrl));
                },
            );
        },
    });

    React.useEffect(() => {
        // The Fasten OAuth runs in an external browser, and the deep-link return does not reliably
        // navigate anywhere, so the user gets stranded on this search screen with no sign that anything
        // happened. When the app returns to the foreground after a connect was started, send them to the
        // connections list and force it to refresh so the new (pending) connection surfaces.
        const subscription = AppState.addEventListener('change', (nextState) => {
            if (nextState === 'active' && connectInitiatedRef.current) {
                connectInitiatedRef.current = false;
                navigation.navigate('connect', {
                    profileId: medicalProfileId,
                    forceFastenRefresh: 'true',
                });
            }
        });

        return () => subscription.remove();
    }, [navigation, medicalProfileId]);

    const handleSelect = React.useCallback(
        (selection: FastenSearchSelection) => {
            pendingMetaRef.current = {
                brandId: selection.brandId,
                brandName: selection.brandName,
                platformType: selection.platformType || 'unknown',
            };

            recordEvent('fastenPortalConnect', {
                profileId: medicalProfileId,
                brandId: selection.brandId,
                portalId: selection.portalId,
            });

            connect({
                brandId: selection.brandId,
                portalId: selection.portalId,
                endpointId: selection.endpointId,
                apiMode: selection.apiMode,
            });
        },
        [connect, recordEvent, medicalProfileId],
    );

    return (
        <>
            <ProfileHeaderContainer profileId={medicalProfileId} omitChatButton>
                <ActionButtonLayout
                    actions={[
                        {
                            title: intl('unableToFindPortal'),
                            onPress: unableToFindPortalPrompt.onPress,
                            show: hasSearched,
                            icon: 'HelpCircle',
                        },
                    ]}
                >
                    <FastenSearch
                        onSelect={handleSelect}
                        isConnecting={isConnecting}
                        onSearchActiveChange={setHasSearched}
                    />
                </ActionButtonLayout>
            </ProfileHeaderContainer>
            {unableToFindPortalPrompt.Prompt}
        </>
    );
};

export default React.memo(AddFastenPortalScreen);
